import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, interval, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BulkSearchRequest, BulkSearchResponse, TaskStatus } from '../shared/models/bulk-search.models';
import { GlobalLoaderService } from '../shared/global-loader/global-loader.service';
import { TaskProgressWebSocketService } from './task-progress-websocket.service';

@Injectable({
  providedIn: 'root'
})
export class BulkSearchService {
  private readonly baseUrl = environment.apiUrl + '/simple-search';
  
  // Progress tracking
  private currentTaskSubject = new BehaviorSubject<TaskStatus | null>(null);
  public currentTask$ = this.currentTaskSubject.asObservable();
  
  private pollingInterval?: any;
  private wsSubscription?: Subscription;
  
  // Bulk search tracking
  private bulkSearchInProgress = false;
  private currentBulkTaskId: string | null = null;
  
  constructor(
    private http: HttpClient,
    private globalLoaderService: GlobalLoaderService,
    private taskProgressWs: TaskProgressWebSocketService
  ) {}
  
  /**
   * Start bulk search. Backend builds all tabs and prompts from filters.
   * @param tabs Optional: run only these tab names (e.g. ['category-analysis'] for single-tab reload).
   */
  async startBulkAnalysis(
    projectId?: string,
    projectdataId?: string,
    filters: Record<string, unknown> = {},
    skipAudits: boolean = false,
    tabs?: string[]
  ): Promise<{ taskId: string; totalTabs: number }> {
    try {
      const request: BulkSearchRequest = {
        filters: filters || {},
        skip_audits: skipAudits,
        ...(tabs?.length ? { tabs } : {})
      };
      if (projectId != null && projectId !== '') {
        request.project_id = projectId;
      }
      if (projectdataId != null && projectdataId !== '') {
        request.projectdata_id = projectdataId;
      }
      
      const response = await this.http.post<BulkSearchResponse>(
        `${this.baseUrl}/bulk-search`,
        request
      ).toPromise();
      
      if (!response?.task_id) {
        throw new Error('No task ID returned from bulk search');
      }
      
      const totalTabs = response.total_tabs ?? 9;
      console.log(`🚀 Started bulk search with task ID: ${response.task_id}, ${totalTabs} tabs`);
      return { taskId: response.task_id, totalTabs };
      
    } catch (error) {
      console.error('Error starting bulk search:', error);
      throw error;
    }
  }
  
  /**
   * Get current status of a bulk search task
   */
  async getTaskStatus(taskId: string): Promise<TaskStatus> {
    try {
      const response = await this.http.get<TaskStatus>(
        `${this.baseUrl}/bulk-search/${taskId}/status`
      ).toPromise();
      
      if (!response) {
        throw new Error('No status returned from server');
      }
      
      return response;
      
    } catch (error: any) {
      console.error(`Error getting task status for ${taskId}:`, error);
      
      // Add status code to error for better handling
      if (error.status) {
        error.status = error.status;
      }
      
      throw error;
    }
  }
  
  /**
   * Start polling for task progress (or WebSocket when useWebSocket).
   * Polling code kept for rollback – do not remove.
   */
  startProgressPolling(taskId: string, onUpdate?: (status: TaskStatus) => void): void {
    if (this.taskProgressWs.useWebSocket) {
      this.wsSubscription?.unsubscribe();
      this.taskProgressWs.connect();
      // Subscribe to messages for this task (data.task_id === taskId)
      this.wsSubscription = this.taskProgressWs.messages$
        .pipe(filter((msg) => (msg.data as any)?.task_id === taskId))
        .subscribe((msg) => {
          const data = msg.data as unknown as TaskStatus;
          if (!data) return;
          this.currentTaskSubject.next(data);
          if (onUpdate) onUpdate(data);
          if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
            this.stopProgressPolling();
            if (data.status === 'completed') console.log('✅ Bulk search completed successfully');
            else if (data.status === 'cancelled') console.log('🛑 Bulk search cancelled');
            else console.error('❌ Bulk search failed:', data.error_message);
          }
        });
      // Initial fetch via REST
      this.getTaskStatus(taskId).then((status) => {
        this.currentTaskSubject.next(status);
        if (onUpdate) onUpdate(status);
        if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') this.stopProgressPolling();
      }).catch(() => {});
      console.log('📊 Starting bulk-search progress via WebSocket for task:', taskId);
      return;
    }

    // Fallback: polling (comment out WebSocket block and set useWebSocket: false to revert)
    if (this.pollingInterval) clearInterval(this.pollingInterval);
    console.log(`📊 Starting progress polling for task: ${taskId}`);
    let pollCount = 0;
    const initialPolls = 3;
    const initialInterval = 2000;
    const regularInterval = 5000;
    const pollTask = async () => {
      try {
        const status = await this.getTaskStatus(taskId);
        this.currentTaskSubject.next(status);
        if (onUpdate) onUpdate(status);
        console.log(`📈 Progress update: ${status.progress_percentage.toFixed(1)}% - ${status.completed_tabs}/${status.total_tabs} tabs`);
        if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
          this.stopProgressPolling();
          if (status.status === 'completed') console.log('✅ Bulk search completed successfully');
          else if (status.status === 'cancelled') console.log('🛑 Bulk search cancelled');
          else console.error('❌ Bulk search failed:', status.error_message);
          return;
        }
        pollCount++;
        if (pollCount === initialPolls) {
          clearInterval(this.pollingInterval!);
          this.pollingInterval = setInterval(pollTask, regularInterval);
        }
      } catch (error) {
        console.error('Error polling task status:', error);
        if ((error as any)?.status === 404) {
          console.warn('Task not found, stopping polling');
          this.stopProgressPolling();
        }
      }
    };
    this.pollingInterval = setInterval(pollTask, initialInterval);
  }

  /**
   * Stop progress polling (or WebSocket subscription).
   */
  stopProgressPolling(): void {
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = undefined;
      console.log('⏹️ Stopped bulk-search WebSocket subscription');
    }
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = undefined;
      console.log('⏹️ Stopped progress polling');
    }
  }
  
  /**
   * Get current task status
   */
  getCurrentTask(): TaskStatus | null {
    return this.currentTaskSubject.value;
  }
  
  /**
   * Clear current task
   */
  clearCurrentTask(): void {
    this.stopProgressPolling();
    this.currentTaskSubject.next(null);
  }
  
  /**
   * Check if currently polling or subscribed via WebSocket
   */
  isPolling(): boolean {
    return !!this.pollingInterval || !!this.wsSubscription;
  }

  /**
   * Check if bulk search is in progress
   */
  isBulkSearchInProgress(): boolean {
    return this.bulkSearchInProgress;
  }

  /**
   * Finalize bulk search after overview analysis completes
   */
  finalizeBulkSearchWithOverview(): void {
    this.bulkSearchInProgress = false;
    this.currentBulkTaskId = null;
    this.globalLoaderService.resetLoader();
  }

  /**
   * Skip audits for a running task
   */
  skipAuditsForTask(taskId: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/bulk-search/${taskId}/skip-audits`, {});
  }
  
  /**
   * Cancel a running bulk search task
   */
  cancelTask(taskId: string): Observable<TaskStatus> {
    return this.http.post<TaskStatus>(`${this.baseUrl}/bulk-search/${taskId}/cancel`, {});
  }

}
